// src/pages/Calendar.jsx
import { useEffect, useState } from "react";
import {
    Container,
    Typography,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Tooltip,
    Grid,
} from "@mui/material";

import SortIcon from "@mui/icons-material/Sort";
import EventIcon from "@mui/icons-material/Event";

import EventCard from "../components/EventCard";

// 🌈 Gradients animés par sport
const SPORT_GRADIENTS = {
    "course à pied": { from: "#ff6f00", to: "#ff8f00", glow: "rgba(255,143,0,0.45)" },
    "ski alpin": { from: "#1976d2", to: "#42a5f5", glow: "rgba(66,165,245,0.45)" },
    telemark: { from: "#6a1b9a", to: "#ab47bc", glow: "rgba(171,71,188,0.45)" },
    pumptrack: { from: "#d32f2f", to: "#ef5350", glow: "rgba(239,83,80,0.45)" },
    vtt: { from: "#2e7d32", to: "#66bb6a", glow: "rgba(102,187,106,0.45)" },
};

// 🔠 Label mois FR
const getMonthLabel = (date) =>
    date.toLocaleString("fr-CH", { month: "long", year: "numeric" });

// 🧠 PARSEUR ROBUSTE
const parseDate = (d) => {
    if (!d) return null;
    const clean = d.replace(/\r/g, "").trim();

    // 1) Format suisse : 12.03.2025
    const swiss = clean.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/);
    if (swiss) {
        let [_, day, month, year] = swiss;
        if (year.length === 2) year = "20" + year;
        return new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`);
    }

    // 2) Format ISO : 2025-03-12
    const iso = clean.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) {
        const [_, year, month, day] = iso;
        return new Date(`${year}-${month}-${day}T00:00:00`);
    }

    // 3) Format slash : 12/03/2025
    const slash = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (slash) {
        let [_, day, month, year] = slash;
        if (year.length === 2) year = "20" + year;
        return new Date(`${year}-${month}-${day}T00:00:00`);
    }

    // 4) Tentative finale
    const fallback = new Date(clean);
    return isNaN(fallback) ? null : fallback;
};

export default function Calendar() {
    const [events, setEvents] = useState([]);
    const [sortOrder, setSortOrder] = useState("asc");
    const [filter, setFilter] = useState("future");

    const SHEET_URL =
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vTmBRUADCXlLDI3Egu8MrIY-Ik5K5N5qx-iRILoCXtwu1abZFFrglSGKn7S4ZAPwcmAAlyeXbx8H1Bp/pub?gid=0&single=true&output=csv";

    useEffect(() => {
        fetch(SHEET_URL)
            .then((res) => res.text())
            .then((csv) => {
                const rows = csv.split("\n").map((r) => r.split(","));
                const headers = rows[0].map((h) => h.trim());

                const data = rows.slice(1).map((row) =>
                    Object.fromEntries(
                        headers.map((h, i) => [
                            h,
                            row[i] ? row[i].replace(/\r/g, "").trim() : "",
                        ])
                    )
                );

                setEvents(data);
            });
    }, []);

    const now = new Date();

    // 🔍 Filtrage robuste
    const filteredEvents = events.filter((ev) => {
        const date = parseDate(ev.date);
        if (!date) return false;
        if (filter === "future") return date >= now;
        if (filter === "past") return date < now;
        return true;
    });

    // 🔥 Tri
    const sortedEvents = [...filteredEvents].sort((a, b) => {
        const da = parseDate(a.date);
        const db = parseDate(b.date);
        if (filter === "past") return db - da;
        return sortOrder === "asc" ? da - db : db - da;
    });

    // 📦 Regroupement par mois
    const eventsByMonth = sortedEvents.reduce((acc, ev) => {
        const d = parseDate(ev.date);
        const key = getMonthLabel(d);
        if (!acc[key]) acc[key] = [];
        acc[key].push(ev);
        return acc;
    }, {});

    return (
        <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <EventIcon sx={{ color: "primary.main", mr: 1 }} />
                <Typography variant="h3">Calendrier des évènements</Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                    alignItems: "center",
                }}
            >
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={(e, val) => val && setFilter(val)}
                >
                    <ToggleButton value="future">À venir</ToggleButton>
                    <ToggleButton value="past">Passés</ToggleButton>
                    <ToggleButton value="all">Tous</ToggleButton>
                </ToggleButtonGroup>

                <Tooltip title="Changer l'ordre">
                    <IconButton
                        onClick={() =>
                            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                        }
                    >
                        <SortIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {Object.entries(eventsByMonth).map(([month, items]) => (
                <Box key={month} sx={{ width: "100%", mb: 4 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            mt: 4,
                            textTransform: "capitalize",
                            borderBottom: "2px solid #ddd",
                            pb: 1,
                        }}
                    >
                        {month}
                    </Typography>

                    <Grid container spacing={3}>
                        {items.map((ev, index) => {
                            const grad =
                                SPORT_GRADIENTS[ev.sport?.toLowerCase()] || {
                                    from: "#777",
                                    to: "#aaa",
                                    glow: "rgba(120,120,120,0.4)",
                                };

                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={ev.nom + index}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        width: "100%",
                                    }}
                                >
                                    <EventCard
                                        ev={ev}
                                        index={index}
                                        gradient={grad}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            ))}
        </Container>
    );
}
